import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Layout from './components/layout/Layout';
import HomePage from './pages/HomePage';
import ProductPage from './pages/ProductPage';
import ProductsPage from './pages/ProductsPage';
import CategoryPage from './pages/CategoryPage';
import WishlistPage from './pages/WishlistPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import SearchPage from './pages/SearchPage';
import TermsPage from './pages/TermsPage';
import ContactPage from './pages/ContactPage';
import { useAuthStore } from './stores/authStore';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  const { i18n } = useTranslation();
  const { checkAuth } = useAuthStore();
  const [hasHydrated, setHasHydrated] = useState(false);

  // Wait for zustand store to rehydrate, then check auth
  useEffect(() => {
    // Subscribe to zustand persist rehydration
    const unsubscribe = useAuthStore.persist.onFinishHydration(() => {
      setHasHydrated(true);
    });

    // Check if already hydrated (in case it finished before subscription)
    if (useAuthStore.persist.hasHydrated()) {
      setHasHydrated(true);
    }

    return unsubscribe;
  }, []);

  // After hydration, check if we need to refresh the auth token
  useEffect(() => {
    if (hasHydrated) {
      const state = useAuthStore.getState();
      if (state.refreshToken && state.isAuthenticated) {
        checkAuth();
      }
    }
  }, [hasHydrated, checkAuth]);

  return (
    <QueryClientProvider client={queryClient}>
      <div
        className="min-h-screen font-sans"
        dir={i18n.dir()}
        lang={i18n.language}
      >
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Layout />}>
              <Route index element={<HomePage />} />
              <Route path="products" element={<ProductsPage />} />
              <Route path="product/:slug" element={<ProductPage />} />
              <Route path="category/:slug" element={<CategoryPage />} />
              <Route path="wishlist" element={<WishlistPage />} />
              <Route path="login" element={<LoginPage />} />
              <Route path="register" element={<RegisterPage />} />
              <Route path="forgot-password" element={<ForgotPasswordPage />} />
              <Route path="reset-password" element={<ResetPasswordPage />} />
              <Route path="search" element={<SearchPage />} />
              <Route path="terms" element={<TermsPage />} />
              <Route path="contact" element={<ContactPage />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </div>
    </QueryClientProvider>
  );
}

export default App;
